/**
 * Domain parsing utilities for determining portal type and region from hostname
 */

/**
 * Parse domain to extract region and provider information
 * @param {string} hostname - The hostname to parse (defaults to window.location.hostname)
 * @returns {Object} Object with region, provider, and other parsed information
 */
export const parseDomain = (hostname = typeof window !== 'undefined' ? window.location.hostname : '') => {
  if (!hostname) {
    return {
      region: null,
      provider: null,
      subdomain: null,
      isLocalhost: false
    };
  }

  // Check if localhost
  if (hostname === 'localhost' || hostname.startsWith('127.0.0.1') || hostname.startsWith('192.168.') || hostname.startsWith('10.')) {
    return {
      region: null,
      provider: null,
      subdomain: null,
      isLocalhost: true
    };
  }

  // Parse subdomain pattern: <region>-<provider>.domain.com
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    const subdomain = parts[0];
    const subdomainParts = subdomain.split('-');
    
    if (subdomainParts.length >= 2) {
      return {
        region: subdomainParts[0],
        provider: subdomainParts[1],
        subdomain: subdomain,
        isLocalhost: false
      };
    }
    
    return {
      region: subdomain,
      provider: null,
      subdomain: subdomain,
      isLocalhost: false
    };
  }

  return {
    region: null,
    provider: null,
    subdomain: null,
    isLocalhost: false
  };
};

/**
 * Map region code to human-readable region name
 * @param {string} regionCode - The region code (e.g., 'weu', 'neu')
 * @returns {string} Human-readable region name
 */
export const mapRegionCodeToName = (regionCode) => {
  if (!regionCode) return 'Unknown';
  
  const regionMap = {
    'weu': 'West Europe',
    'neu': 'North Europe',
    'eastus': 'East US',
    'westus': 'West US',
    'eastus2': 'East US 2',
    'westus2': 'West US 2',
    'centralus': 'Central US',
    'southcentralus': 'South Central US',
    'northcentralus': 'North Central US',
    'westcentralus': 'West Central US',
    'canadaeast': 'Canada East',
    'canadacentral': 'Canada Central',
    'brazilsouth': 'Brazil South',
    'southeastasia': 'Southeast Asia',
    'eastasia': 'East Asia',
    'australiaeast': 'Australia East',
    'australiasoutheast': 'Australia Southeast',
    'japaneast': 'Japan East',
    'japanwest': 'Japan West',
    'koreacentral': 'Korea Central',
    'koreasouth': 'Korea South',
    'indiawest': 'India West',
    'indiasouth': 'India South',
    'indiacentral': 'India Central',
    'southafricanorth': 'South Africa North',
    'uaenorth': 'UAE North',
    'ukwest': 'UK West',
    'uksouth': 'UK South',
    'francecentral': 'France Central',
    'francesouth': 'France South',
    'germanywestcentral': 'Germany West Central',
    'germanynorth': 'Germany North',
    'norwayeast': 'Norway East',
    'switzerlandnorth': 'Switzerland North',
    'switzerlandwest': 'Switzerland West',
    'swedencentral': 'Sweden Central',
    'polandcentral': 'Poland Central',
    'italynorth': 'Italy North'
  };

  return regionMap[regionCode.toLowerCase()] || regionCode;
};

/**
 * Determine if the current portal is the auth portal
 * @param {string} hostname - The hostname to check (defaults to window.location.hostname)
 * @returns {boolean} True if this is the auth portal
 */
export const isAuthPortal = (hostname = typeof window !== 'undefined' ? window.location.hostname : '') => {
  if (!hostname) return false;
  
  // Check for auth portal patterns
  // Auth portal typically has 'auth' in subdomain or path
  // eslint-disable-next-line no-unused-vars
  const parsed = parseDomain(hostname);
  
  // Check if hostname contains 'auth'
  if (hostname.includes('auth')) {
    return true;
  }
  
  // Check port - auth portal typically runs on 3002
  if (typeof window !== 'undefined' && window.location.port === '3002') {
    return true;
  }
  
  return false;
};
